import { COOKIE_CONSENT_KEY } from '@/constants/cookie-consent';
import { deleteCookie, getCookie, setBrowserCookie } from '@/utils/cookies';

import type {
  ConsentChoices,
  ConsentDecisionSource,
  ConsentSource,
  ConsentSignals,
} from './consent-signals';

import {
  CONSENT_SIGNAL_KEYS,
  CURRENT_CONSENT_VERSION,
  UNSET_ALL_CHOICES,
  choicesFromLegacySignals,
  coerceConsentSignals,
  isConsentChoiceValue,
  isConsentSource,
  mergeChoices,
  OPTIONAL_CONSENT_CATEGORIES,
  reconcileChoicesDenyWins,
  signalsFromChoices,
} from './consent-signals';

/**
 * Domain-wide consent cookie shared with the marketing site and the legacy
 * researcher app. Source of truth for consent on every `*.respondent.io`
 * surface.
 *
 * Shape:
 * `{ "v": 2, "at": "<iso>", "src": "banner"|"settings"|"implied",
 *    "choices": { functional, analytics, adMeasurement, adPersonalization } }`
 * each choice being `"granted" | "denied" | "unset"`.
 */
export const CONSENT_COOKIE_KEY = 'rio_consent';

/**
 * Dual-written gtag-shaped payload, for one release, so anything still
 * reading `consentMode` (GTM tags, a cached marketing page) keeps working.
 * Derived from `rio_consent` with `unset` collapsed to `denied`. Drop this
 * write once nothing reads it.
 */
export const CONSENT_MODE_COOKIE_KEY = 'consentMode';

/**
 * Version/timestamp sibling of a pre-`rio_consent` decision. Read during
 * migration; no longer written.
 */
export const CONSENT_META_COOKIE_KEY = 'rio_consent_meta';

const CONSENT_COOKIE_ROOT_DOMAIN = 'respondent.io';

/**
 * Matches the legacy writer's expiry. Modern browsers clamp script-written
 * cookies to ~400 days regardless, so the practical lifetime is theirs to
 * decide; what matters here is that both writers agree on the contract.
 */
const CONSENT_COOKIE_MAX_AGE_SECONDS = 100 * 365 * 24 * 60 * 60;

export type StoredConsent = {
  v: number;
  at: string;
  src: ConsentSource;
  choices: ConsentChoices;
};

/**
 * A stored record plus what this app is allowed to do with it.
 */
export type ResolvedConsent = StoredConsent & {
  /**
   * Whether the record may be written through to the account. True for a
   * decision recorded under this consent model: `rio_consent`, a `consentMode`
   * cookie stamped with its `rio_consent_meta` sibling, this app's own
   * `rio_cookie_consent`, or the profile copy itself. False for one
   * reconstructed in memory from a pre-unification writer — persisting that
   * would turn a merge of cookies of unknown age into an account decision.
   */
  isSyncable: boolean;
  /**
   * Whether two sources held opposite answers and the denial was kept. The
   * surviving denial is the safe reading of a disagreement rather than an
   * answer the visitor is known to have given last, so the prompt is shown and
   * a grant can be given deliberately.
   */
  isReconciled: boolean;
};

/** A decision recorded under this model: authoritative, and safe to persist. */
function recordedDecision(stored: StoredConsent): ResolvedConsent {
  return { ...stored, isSyncable: true, isReconciled: false };
}

/**
 * What this app knows about the visitor's consent from the signed-in
 * profile, used when this browser has no cookie.
 */
export type AppLocalConsent = {
  profileChoices: ConsentChoices | null;
  profileConsentVersion: number | null;
};

/**
 * Returns the domain to scope the consent cookies to, or `undefined` for a
 * host-only cookie.
 *
 * Browsers silently drop a `Domain=` the current host is not under, which
 * would make consent writes vanish on localhost and preview hosts. Deriving
 * the domain from the actual hostname keeps local development working with
 * the same code path as production.
 */
export function resolveConsentCookieDomain(
  hostname: string | undefined,
): string | undefined {
  if (!hostname) return undefined;
  if (
    hostname === CONSENT_COOKIE_ROOT_DOMAIN ||
    hostname.endsWith(`.${CONSENT_COOKIE_ROOT_DOMAIN}`)
  ) {
    return `.${CONSENT_COOKIE_ROOT_DOMAIN}`;
  }
  return undefined;
}

function currentConsentCookieDomain(): string | undefined {
  return resolveConsentCookieDomain(globalThis.location?.hostname);
}

function parseJsonCookie(name: string): unknown {
  const raw = getCookie(name);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    // A malformed cookie is treated as absent, which means no stored
    // decision and a re-prompt. Never throw during bootstrap.
    return null;
  }
}

function coerceChoices(value: unknown): ConsentChoices | null {
  if (typeof value !== 'object' || value === null) return null;
  const record: Record<string, unknown> = { ...value };
  const choices: ConsentChoices = { ...UNSET_ALL_CHOICES };
  for (const category of OPTIONAL_CONSENT_CATEGORIES) {
    const candidate = record[category];
    if (isConsentChoiceValue(candidate)) choices[category] = candidate;
  }
  return choices;
}

function coerceConsentRecord(value: unknown): StoredConsent | null {
  if (typeof value !== 'object' || value === null) return null;
  const record: Record<string, unknown> = { ...value };
  if (typeof record.v !== 'number' || !Number.isFinite(record.v)) return null;
  if (typeof record.at !== 'string') return null;
  if (!isConsentSource(record.src)) return null;
  const choices = coerceChoices(record.choices);
  if (!choices) return null;
  return { v: record.v, at: record.at, src: record.src, choices };
}

/**
 * Reads `rio_consent` as a decision. Returns `null` when no choice is
 * recorded, which includes an `src: 'implied'` record: that is a region
 * default a past build persisted, not something the visitor answered, so it is
 * read as absent everywhere — it cannot be merged into a new write, cannot
 * suppress the prompt, and cannot be synced to the account.
 */
export function readConsentCookie(): StoredConsent | null {
  const record = readAnyConsentCookie();
  return record?.src === 'implied' ? null : record;
}

/**
 * The `rio_consent` cookie whatever its `src`. Only the resolver needs this,
 * to tell "no cookie" apart from "an implied cookie" — the two are the same
 * decision (none) but not the same evidence about the sibling `consentMode`.
 */
function readAnyConsentCookie(): StoredConsent | null {
  return coerceConsentRecord(parseJsonCookie(CONSENT_COOKIE_KEY));
}

function readConsentMetaVersion(): number | null {
  const parsed = parseJsonCookie(CONSENT_META_COOKIE_KEY);
  if (typeof parsed !== 'object' || parsed === null) return null;
  const record: Record<string, unknown> = { ...parsed };
  const version = record.consentVersion;
  if (typeof version !== 'number' || !Number.isFinite(version)) return null;
  return version;
}

/**
 * Whether a parsed `consentMode` payload carries a gtag signal at all. Only
 * the known signal keys count: an unrelated key holding `granted` is not a
 * consent signal, and reading one as a decision would hide `rio_cookie_consent`
 * and the profile copy behind an all-`unset` record.
 */
function hasAnyConsentSignal(record: Record<string, unknown>): boolean {
  return CONSENT_SIGNAL_KEYS.some((key) => {
    const value = record[key];
    return value === 'granted' || value === 'denied';
  });
}

/**
 * In-memory record from a `consentMode` cookie written before `rio_consent`.
 * Missing signals map to `unset`, not `denied`, and the cookie keeps its own
 * version, so a visitor left with an unanswered category still gets the
 * version re-prompt.
 *
 * The `rio_consent_meta` sibling is what makes such a cookie authoritative:
 * every writer that speaks this model writes both, so its presence means the
 * decision was recorded here and outranks everything else.
 *
 * Without the sibling the cookie comes from a pre-unification writer whose age
 * is unknown, so it cannot outrank what this app recorded: a marketing-site
 * "accept all" would otherwise overturn a denial held in `rio_cookie_consent`
 * or on the profile. Those sources are reconciled with deny winning, in
 * memory, and the result is not written and not syncable — it stands in for a
 * decision only until the visitor makes one.
 */
function readLegacyConsentMode(
  appLocal: AppLocalConsent,
): ResolvedConsent | null {
  const parsed = parseJsonCookie(CONSENT_MODE_COOKIE_KEY);
  if (typeof parsed !== 'object' || parsed === null) return null;
  const record: Record<string, unknown> = { ...parsed };
  if (coerceConsentSignals(parsed) === null) return null;
  if (!hasAnyConsentSignal(record)) return null;

  const cookieChoices = choicesFromLegacySignals(record);
  const metaVersion = readConsentMetaVersion();
  if (metaVersion !== null) {
    return recordedDecision({
      v: metaVersion,
      at: new Date(0).toISOString(),
      src: 'banner',
      choices: cookieChoices,
    });
  }

  const { choices, hasConflict } = reconcileChoicesDenyWins([
    cookieChoices,
    ...appLocalChoices(appLocal),
  ]);
  return {
    v: 1,
    at: new Date(0).toISOString(),
    src: 'banner',
    choices,
    isSyncable: false,
    isReconciled: hasConflict,
  };
}

/** The answers this app itself recorded: its own cookie, then the profile. */
function appLocalChoices(appLocal: AppLocalConsent): ConsentChoices[] {
  const sources: ConsentChoices[] = [];
  const legacyAnalytics = readLegacyAnalyticsCookie();
  if (legacyAnalytics) sources.push(legacyAnalytics.choices);
  const profile = readProfileConsent(appLocal);
  if (profile) sources.push(profile.choices);
  return sources;
}

/**
 * The app-local analytics choice, from the cookie that predates the shared
 * one. `null` when this browser has no such record.
 */
export function readLegacyConsentChoice(): boolean | null {
  const legacy = getCookie(COOKIE_CONSENT_KEY);
  if (legacy !== 'granted' && legacy !== 'denied') return null;
  return legacy === 'granted';
}

function readLegacyAnalyticsCookie(): ResolvedConsent | null {
  const legacyAnalytics = readLegacyConsentChoice();
  if (legacyAnalytics === null) return null;
  return recordedDecision({
    v: 1,
    at: new Date(0).toISOString(),
    src: 'banner',
    choices: {
      ...UNSET_ALL_CHOICES,
      analytics: legacyAnalytics ? 'granted' : 'denied',
    },
  });
}

function readProfileConsent(appLocal: AppLocalConsent): ResolvedConsent | null {
  if (!appLocal.profileChoices) return null;
  return recordedDecision({
    v: appLocal.profileConsentVersion ?? 1,
    at: new Date(0).toISOString(),
    src: 'settings',
    choices: appLocal.profileChoices,
  });
}

function forgetLegacyConsentCookie(): void {
  deleteCookie(COOKIE_CONSENT_KEY);
}

/**
 * The `consentMode` payload that accompanies a `rio_consent` record: its
 * choices as gtag signals, `unset` collapsed to `denied`.
 *
 * Both the dual-write below and the sibling comparison in
 * {@link resolveStoredConsent} take the shape from here, so the write and the
 * check for whether a cookie is that write's copy cannot describe different
 * things.
 */
function dualWrittenConsentMode(record: StoredConsent): ConsentSignals {
  return signalsFromChoices(record.choices);
}

function writeDualWrittenConsentMode(record: StoredConsent, domain?: string) {
  setBrowserCookie(
    CONSENT_MODE_COOKIE_KEY,
    JSON.stringify(dualWrittenConsentMode(record)),
    { maxAgeSeconds: CONSENT_COOKIE_MAX_AGE_SECONDS, domain },
  );
}

/**
 * Whether the `consentMode` cookie still holds what `record`'s own write put
 * there.
 *
 * Compared signal by signal rather than as text: a re-encode or a different
 * key order is not evidence of another decision, and a differing value is.
 */
function isDualWrittenConsentMode(record: StoredConsent): boolean {
  const sibling = coerceConsentSignals(
    parseJsonCookie(CONSENT_MODE_COOKIE_KEY),
  );
  if (!sibling) return false;
  const dualWritten = dualWrittenConsentMode(record);
  return CONSENT_SIGNAL_KEYS.every((key) => sibling[key] === dualWritten[key]);
}

/**
 * Writes `rio_consent` (and dual-writes `consentMode`) on the shared domain,
 * returning the record as the decision now in force.
 *
 * Incoming `unset` values do not overwrite a stored `granted` or `denied`.
 * Only a real decision can be written — the banner or the settings panel.
 * `src` excludes `implied` so an inferred default cannot be stamped as one,
 * and a bare read must not call this at all.
 */
export function writeConsentCookie(
  choices: ConsentChoices,
  src: ConsentDecisionSource,
  consentVersion: number = CURRENT_CONSENT_VERSION,
): ResolvedConsent {
  const domain = currentConsentCookieDomain();
  const existing = readConsentCookie();
  const merged = mergeChoices(existing?.choices ?? UNSET_ALL_CHOICES, choices);
  const record: StoredConsent = {
    v: consentVersion,
    at: new Date().toISOString(),
    src,
    choices: merged,
  };

  setBrowserCookie(CONSENT_COOKIE_KEY, JSON.stringify(record), {
    maxAgeSeconds: CONSENT_COOKIE_MAX_AGE_SECONDS,
    domain,
  });
  writeDualWrittenConsentMode(record, domain);
  forgetLegacyConsentCookie();

  return recordedDecision(record);
}

/**
 * The single entry point for "what has this browser consented to".
 *
 * Read order, first hit wins:
 * 1. `rio_consent`, unless it is `src: 'implied'` — see below
 * 2. `consentMode` (an explicit `ad_storage` answers both ad categories).
 *    With its `rio_consent_meta` sibling it is authoritative; without it, it
 *    is reconciled against steps 3 and 4 with deny winning.
 * 3. `rio_cookie_consent` (analytics only; everything else `unset`)
 * 4. the signed-in profile copy
 * 5. nothing — the caller applies `decideConsentDefaults`
 *
 * An `src: 'implied'` cookie skips step 2 too, but only while `consentMode`
 * still holds what that record dual-wrote. Such a record exists only because
 * an earlier build persisted the region default, and that build wrote both
 * cookies from the same values — so a matching sibling is a copy of the
 * overlay rather than independent evidence, and reading it would let the same
 * inferred grants hide the prompt through the other cookie.
 *
 * A sibling holding anything else was written after it by a surface that
 * leaves `rio_consent` alone — the legacy researcher app and the signup app
 * both write `consentMode` and nothing more. That is a later decision, so it
 * is read as any other unstamped legacy cookie: reconciled against steps 3
 * and 4 with deny winning, which is what keeps an opt-out recorded elsewhere
 * from being discarded in favour of the inferred grants.
 *
 * Never writes. An unanswered prompt is not a decision, and stamping a
 * cookie here would record consent the visitor never gave.
 */
export function resolveStoredConsent(
  appLocal?: AppLocalConsent,
): ResolvedConsent | null {
  const local: AppLocalConsent = appLocal ?? {
    profileChoices: null,
    profileConsentVersion: null,
  };

  const cookie = readAnyConsentCookie();
  if (cookie && cookie.src !== 'implied') return recordedDecision(cookie);

  // Any cookie still in hand is an implied one. Step 2 is skipped only while
  // the `consentMode` beside it is that same write's copy; a sibling that
  // differs is a decision another surface recorded afterwards, and must be
  // honoured.
  const legacyConsentMode =
    cookie && isDualWrittenConsentMode(cookie)
      ? null
      : readLegacyConsentMode(local);

  return (
    legacyConsentMode ??
    readLegacyAnalyticsCookie() ??
    readProfileConsent(local) ??
    null
  );
}
